import {
  Box,
  Menu,
  MenuItem,
  IconButton
} from '@mui/material'
import MapIcon from '@mui/icons-material/Map'
import TableChartIcon from '@mui/icons-material/TableChart'
import DescriptionIcon from '@mui/icons-material/Description'
import { useEffect, useState } from 'react'
import { sendGet } from 'hooks/use-fetch'
import { type SelectOption } from 'Components/SelectWithLabel'
import { toast } from 'react-toastify'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { type GSRPCapacityReportModel } from 'Pages/Reports/GSRPCapacityReport.types'
import { GSRPCapacityReportTableView } from 'Pages/Reports/GSRPCapacityReportTableView'
import { GSRPCapacityReportMapView } from 'Pages/Reports/GSRPCapacityReportMapView'
import { GSRPCapacityReportPDFView } from 'Pages/Reports/GSRPCapacityReportPDFView'
import { PublicReportLayout } from 'Components/PublicReportLayout'
import { getShortYear } from 'Pages/Reports/GSRPCapacityReport.utils'
import { AccountType } from 'core/types'

type ViewMode = 'table' | 'map' | 'pdf'

interface TimespanModel {
  id: number
  name: string
  startDate: string | null
}

export function GSRPCapacityReport (): JSX.Element {
  const accountId = useAccountId()
  const [providers, setProviders] = useState<Array<SelectOption<number>>>([])
  const [providerID, setProviderID] = useState<number | string>('')
  const [reportData, setReportData] = useState<GSRPCapacityReportModel[]>([])
  const [loading, setLoading] = useState(false)
  const [timespans, setTimespans] = useState<TimespanModel[]>([])
  const [selectedTimespanID, setSelectedTimespanID] = useState<number | null>(null)
  const [timespanMenuAnchor, setTimespanMenuAnchor] = useState<null | HTMLElement>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('map')
  const [viewModeMenuAnchor, setViewModeMenuAnchor] = useState<null | HTMLElement>(null)

  // Parse URL parameters once
  const urlParams = new URLSearchParams(window.location.search)
  const isdFromQuery = urlParams.get('ISDID')

  // Get ISD ID from query string or user context
  const isdID = isdFromQuery !== null ? Number(isdFromQuery) : (accountId?.type === AccountType.ISD ? accountId?.id : accountId?.currentISDID ?? undefined)

  // Determine if this is public access (via URL parameter) vs logged-in access
  const isPublicAccess = isdFromQuery !== null

  // Fetch timespans on mount
  useEffect(() => {
    const fetchTimespans = async (): Promise<void> => {
      const { response, success } = await sendGet('/Report/GSRPCapacityReport/GetPublicTimespans', {})
      if (success && Array.isArray(response)) {
        setTimespans(response)

        // Default to timespan that begins in current year
        const currentYear = new Date().getFullYear()
        const defaultTimespan = response.find((ts: TimespanModel) => {
          if (ts.startDate != null) {
            const startYear = new Date(ts.startDate).getFullYear()
            return startYear === currentYear
          }
          return false
        })

        if (defaultTimespan != null) {
          setSelectedTimespanID(defaultTimespan.id)
        } else if (response.length > 0) {
          // Fallback to first timespan if no match
          setSelectedTimespanID(response[0].id)
        }
      }
    }
    const fetchProviders = async (): Promise<void> => {
      if (isdID !== undefined) {
        const { response } = await sendGet('/Reporting/ISDServiceProviders', {})
        setProviders(response)
      }
    }
    void fetchProviders()
    void fetchTimespans()
  }, [])

  const fetchReport = async (): Promise<void> => {
    if (isdID === undefined || selectedTimespanID === null) {
      return
    }

    setLoading(true)
    try {
      const url = providerID !== '' && providerID !== undefined
        ? `/Report/GSRPCapacityReport/GetPublicGSRPCapacityReport?isdID=${isdID}&timespanID=${selectedTimespanID}&serviceProviderID=${providerID}`
        : `/Report/GSRPCapacityReport/GetPublicGSRPCapacityReport?isdID=${isdID}&timespanID=${selectedTimespanID}`

      const { response, success } = await sendGet(url, {})

      if (success) {
        setReportData(response)
      } else {
        toast.error('Failed to fetch report data.' + url)
      }
    } catch (error) {
      toast.error('An error occurred while fetching the report.')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  // Auto-fetch report when component loads or filters change
  useEffect(() => {
    void fetchReport()
  }, [isdID, providerID, selectedTimespanID])

  const selectedTimespan = timespans.find(ts => ts.id === selectedTimespanID)
  const isdName = reportData.length > 0 ? reportData[0].isdName : undefined

  // Get icon for current view mode
  const getViewIcon = (): JSX.Element => {
    switch (viewMode) {
      case 'map':
        return <MapIcon />
      case 'table':
        return <TableChartIcon />
      case 'pdf':
        return <DescriptionIcon />
    }
  }

  return (
    <PublicReportLayout
      title="GSRP Seat Availability"
      subtitle={isdName}
      isLoggedIn={!isPublicAccess}
      actions={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Year selector dropdown */}
          <IconButton
            onClick={(e) => { setTimespanMenuAnchor(e.currentTarget) }}
            sx={{
              border: '1px solid #E1E1E1',
              borderRadius: 1,
              width: 48,
              height: 48,
              bgcolor: 'white',
              '&:hover': {
                bgcolor: '#F5F5F5'
              }
            }}
          >
            <Box sx={{ fontSize: '0.75rem', fontWeight: 'bold', lineHeight: 1.2, textAlign: 'center' }}>
              {selectedTimespan != null ? getShortYear(selectedTimespan.name) : '26/27'}
            </Box>
          </IconButton>
          <Menu
            anchorEl={timespanMenuAnchor}
            open={Boolean(timespanMenuAnchor)}
            onClose={() => { setTimespanMenuAnchor(null) }}
          >
            {timespans.map((ts) => (
              <MenuItem
                key={ts.id}
                onClick={() => {
                  setSelectedTimespanID(ts.id)
                  setTimespanMenuAnchor(null)
                }}
                selected={ts.id === selectedTimespanID}
              >
                {ts.name}
              </MenuItem>
            ))}
          </Menu>

          {/* View mode selector dropdown */}
          {reportData.length > 0 && (
            <>
              <IconButton
                onClick={(e) => { setViewModeMenuAnchor(e.currentTarget) }}
                sx={{
                  border: '1px solid #E1E1E1',
                  borderRadius: 1,
                  width: 48,
                  height: 48,
                  bgcolor: 'white',
                  '&:hover': {
                    bgcolor: '#F5F5F5'
                  }
                }}
              >
                {getViewIcon()}
              </IconButton>
              <Menu
                anchorEl={viewModeMenuAnchor}
                open={Boolean(viewModeMenuAnchor)}
                onClose={() => { setViewModeMenuAnchor(null) }}
              >
                <MenuItem
                  onClick={() => {
                    setViewMode('map')
                    setViewModeMenuAnchor(null)
                  }}
                  selected={viewMode === 'map'}
                >
                  <MapIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                  Map View
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setViewMode('table')
                    setViewModeMenuAnchor(null)
                  }}
                  selected={viewMode === 'table'}
                >
                  <TableChartIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                  Table View
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setViewMode('pdf')
                    setViewModeMenuAnchor(null)
                  }}
                  selected={viewMode === 'pdf'}
                >
                  <DescriptionIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                  Report View
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      }
    >
      <Box>

        {/* Render appropriate view */}
        {reportData.length > 0 && viewMode === 'table' && (
          <GSRPCapacityReportTableView
            reportData={reportData}
            providers={providers}
            providerID={providerID}
            setProviderID={setProviderID}
            isdID={isdID}
            loading={loading}
          />
        )}

        {reportData.length > 0 && viewMode === 'map' && (
          <GSRPCapacityReportMapView
            reportData={reportData}
          />
        )}

        {reportData.length > 0 && viewMode === 'pdf' && (
          <GSRPCapacityReportPDFView
            reportData={reportData}
          />
        )}
      </Box>
    </PublicReportLayout>
  )
}
